import { ErpFrame } from '@ca/common'
import {
  FrameVariableKeys,
  OrderValues,
  PackageType,
  RemoteConfig,
} from '@ca/common/types'
import { OrderFrame } from '@order/orderTypes'
import { debugConditions } from '@order/utils/getDebugLog'
import { mergeValues } from '@order/utils/OrderVariables'
import ConfigConditions from 'features/config/ConfigConditions'
import { get } from 'lodash'
import MeasurementUtils from 'utils/MeasurementUtils'

/**
 * Resolves the first matching condition name for a frame-scoped condition group.
 * Returns null if no group is configured, values are missing, or no condition matches.
 */
const resolveFrameCondition = (
  groupKey: string,
  frame: OrderFrame | ErpFrame,
  values: OrderValues,
  config: RemoteConfig
): string | null => {
  const group = ConfigConditions.group.get(groupKey, config)
  if (!group.length || !config.conditions) {
    return null
  }
  const resolvedValues = {
    values: mergeValues(values.values, getFrameValues(frame)),
    refraction: values.refraction,
  }
  const check = (name: string) =>
    ConfigConditions.check(
      config.configConditions?.[name],
      resolvedValues,
      config.conditions
    )
  const { valid } = ConfigConditions.group.check(check, group)
  return valid[0]?.name ?? null
}

const getFramePrice = ({
  frame,
  values,
  config,
}: {
  frame: OrderFrame | ErpFrame
  values: OrderValues | undefined | null
  config: RemoteConfig | undefined | null
}) => {
  if (!config || !values) {
    return frame.list_price
  }

  const matchedName = resolveFrameCondition('frame:price', frame, values, config)
  if (!matchedName) {
    return frame.list_price
  }

  const settings = ConfigConditions.value(matchedName, 'framePrice', config)

  if (typeof settings?.price === 'number') {
    return settings.price
  }
  if (settings?.key) {
    const value = get(frame, settings.key)

    if (typeof value === 'string') {
      const parsed = parseFloat(value)
      if (Number.isFinite(parsed)) {
        return parsed
      }
    }
    if (typeof value === 'number') {
      return value
    }
  }
  return frame.list_price
}

const getFrameLimits = ({
  frame,
  values,
  config,
  minPrice,
  maxPrice,
}: {
  frame: OrderFrame | ErpFrame
  values: OrderValues | undefined | null
  config: RemoteConfig | undefined | null
  minPrice?: number
  maxPrice?: number
}) => {
  const fallback = { minPrice, maxPrice }
  if (!config || !values) {
    return fallback
  }

  const matchedName = resolveFrameCondition('frame:limits', frame, values, config)
  if (!matchedName) {
    return fallback
  }

  const settings = ConfigConditions.value(matchedName, 'frameLimits', config)
  if (!settings) {
    return fallback
  }

  return {
    minPrice: typeof settings.minPrice === 'number' ? settings.minPrice : minPrice,
    maxPrice: typeof settings.maxPrice === 'number' ? settings.maxPrice : maxPrice,
  }
}

const getFrameValues = (frame: OrderFrame | ErpFrame) => {
  const { id, list_price, default_code, vbox } = frame

  const values: Record<string, string | number> = {
    [FrameVariableKeys.id]: id,
    [FrameVariableKeys.vbox]: vbox,
    [FrameVariableKeys.price]: list_price,
    [FrameVariableKeys.type]: frame.ftype,
    [FrameVariableKeys.code]: default_code,
    [FrameVariableKeys.qty]: frame.qty,
    [FrameVariableKeys.store_qty]: frame.store_qty,
    [FrameVariableKeys.remote_qty]: frame.qty - frame.store_qty,
    [FrameVariableKeys.total_qty]: frame.qty,
    [FrameVariableKeys.plc]: frame.plc,
  }

  // Dynamically add all other frame values
  Object.entries(frame).forEach(([key, value]) => {
    if (typeof value === 'string' || typeof value === 'number') {
      values[`frame_${key}`] = value
    }
  })

  // Add all frame attributes
  if (frame.attributes) {
    Object.entries(frame.attributes).forEach(([key, value]) => {
      values[`frame_attributes_${key}`] = value
    })
  }

  // example values:
  // frame_default_code: '123456'
  // frame_plc: 'ONS'
  // frame_qty: 1
  // frame_attributes_fcatalog: 'GOLD-F'

  return values
}

// Part of the frame values and also used in the order payload
const getFrameHeights = (frame?: OrderFrame) => {
  if (!frame) {
    return {
      r_fh: 0,
      l_fh: 0,
    }
  }
  const sh = frame.measurement?.segmentHeight
  if (!sh) {
    return {
      r_fh: 0,
      l_fh: 0,
    }
  }
  return {
    r_fh: MeasurementUtils.parse.sh.value(sh?.rightSH),
    l_fh: MeasurementUtils.parse.sh.value(sh?.leftSH),
  }
}

const getAdjustedVbox = (frame: OrderFrame, type: PackageType = 'sv') => {
  const { vbox, shape } = frame
  const offset = type === 'sv' ? 2 : 10

  const y_ext = shape.extend_y || 0
  const value = vbox ? vbox + y_ext - offset : undefined
  debugConditions.log('getAdjustedVbox', value, frame)
  return value
}

const getFramePayload = (frame: OrderFrame | undefined, type: PackageType) => {
  if (!frame) {
    return null
  }

  return {
    adjusted_vbox: getAdjustedVbox(frame, type),
    ...getFrameValues(frame),
    ...getFrameHeights(frame),
  }
}

export type FrameQty = {
  total: number
  store: number
  remote: number
}

const getFrameQty = (frame: Pick<ErpFrame, 'qty' | 'store_qty'>): FrameQty => {
  return {
    total: frame.qty,
    store: frame.store_qty,
    remote: Math.max(0, frame.qty - frame.store_qty),
  }
}

const FrameUtils = {
  getValues: getFrameValues,
  getHeights: getFrameHeights,
  getAdjustedVbox,
  getPayload: getFramePayload,
  getQty: getFrameQty,
  getPrice: getFramePrice,
  getLimits: getFrameLimits,
}

export default FrameUtils
